import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@volca/ui/lib/utils";

export const textVariants = cva("", {
  variants: {
    variant: {
      caption: "text-xs leading-[1.45]",
      body: "text-sm leading-[1.5]",
      label: "text-sm leading-snug font-medium",
      title: "text-base leading-[1.3] font-medium",
      heading:
        "text-xl leading-[1.25] font-semibold tracking-[-0.02em] text-balance",
      display:
        "text-2xl leading-[1.2] font-semibold tracking-[-0.02em] text-balance",
      stat: "text-3xl leading-[1.1] font-medium tracking-[-0.02em] tabular-nums",
      "stat-lg":
        "text-5xl leading-[1.05] font-medium tracking-[-0.03em] tabular-nums",
    },
    tone: {
      default: "text-ink",
      muted: "text-subtle",
      brand: "text-brand",
      link: "text-link",
      success: "text-success",
      destructive: "text-danger",
      inverse: "text-on-brand",
    },
    weight: {
      normal: "font-normal",
      medium: "font-medium",
      semibold: "font-semibold",
      bold: "font-bold",
    },
    truncate: { true: "truncate" },
    tabular: { true: "tabular-nums" },
  },
  defaultVariants: { variant: "body" },
});

export interface TextProps
  extends useRender.ComponentProps<"p">,
    VariantProps<typeof textVariants> {}

export const Text = ({
  variant,
  tone,
  weight,
  truncate,
  tabular,
  className,
  render,
  ref,
  ...props
}: TextProps) =>
  useRender({
    defaultTagName: "p",
    render,
    ref,
    props: mergeProps<"p">(
      {
        className: cn(
          textVariants({ variant, tone, weight, truncate, tabular }),
          className,
        ),
      },
      props,
    ),
  });

const HEADING_VARIANT = {
  1: "display",
  2: "heading",
  3: "title",
  4: "label",
} as const;

export interface HeadingProps
  extends Omit<TextProps, "render">,
    useRender.ComponentProps<"h2"> {
  level?: 1 | 2 | 3 | 4;
}

export const Heading = ({
  level = 2,
  variant,
  tone = "default",
  weight,
  truncate,
  tabular,
  className,
  render,
  ref,
  ...props
}: HeadingProps) =>
  useRender({
    defaultTagName: `h${level}`,
    render,
    ref,
    props: mergeProps<"h2">(
      {
        className: cn(
          textVariants({
            variant: variant ?? HEADING_VARIANT[level],
            tone,
            weight,
            truncate,
            tabular,
          }),
          className,
        ),
      },
      props,
    ),
  });
